import { recipeService } from './recipeService';

/**
 * Service pour récupérer des recettes externes et les importer dans notre base de données
 */
const THEMEALDB_API = 'https://www.themealdb.com/api/json/v1/1';

/**
 * Récupère des recettes avec un ingrédient spécifique depuis TheMealDB
 * @param {string} ingredient - L'ingrédient à rechercher (ex: "chocolate")
 * @returns {Promise<Array>} - Les recettes trouvées
 */
export const fetchRecipesByIngredient = async (ingredient) => {
  try {
    console.log(`Recherche de recettes avec l'ingrédient: ${ingredient}`);
    const response = await fetch(`${THEMEALDB_API}/filter.php?i=${ingredient}`);
    const data = await response.json();
    
    if (!data.meals) {
      console.warn(`Aucune recette trouvée pour l'ingrédient: ${ingredient}`);
      return [];
    }
    
    return data.meals || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des recettes:', error);
    return [];
  }
};

/**
 * Récupère les détails d'une recette par son ID
 * @param {string} id - L'ID de la recette dans TheMealDB
 * @returns {Promise<Object>} - Les détails de la recette
 */
export const fetchRecipeDetails = async (id) => {
  try {
    // Vérifier si c'est une recette de secours
    if (id.startsWith('fallback')) {
      console.log(`Utilisation des détails de secours pour la recette: ${id}`);
      return createFallbackRecipeDetails(id);
    }
    
    // Sinon, appeler l'API TheMealDB
    const response = await fetch(`${THEMEALDB_API}/lookup.php?i=${id}`);
    const data = await response.json();
    
    if (!data.meals?.[0]) {
      console.warn(`Aucun détail trouvé pour la recette: ${id}, utilisation des détails de secours`);
      return createFallbackRecipeDetails(id);
    }
    
    return data.meals[0];
  } catch (error) {
    console.error('Erreur lors de la récupération des détails de la recette:', error);
    return createFallbackRecipeDetails(id);
  }
};

/**
 * Crée des détails de recette de secours
 * @param {string} id - L'ID de la recette de secours
 * @returns {Object} - Les détails de la recette de secours
 */
const createFallbackRecipeDetails = (id) => {
  const fallbackRecipes = getFallbackRecipes();
  const recipe = fallbackRecipes.find((recipe) => recipe.id === id);
  
  if (!recipe) {
    console.error(`Recette de secours non trouvée pour l'ID: ${id}`);
    return null;
  }
  
  return {
    idMeal: recipe.id,
    strMeal: recipe.strMeal,
    strMealThumb: recipe.strMealThumb,
    strInstructions: 'Instructions de secours',
    strIngredient1: 'Ingrédient de secours 1',
    strIngredient2: 'Ingrédient de secours 2',
    strIngredient3: 'Ingrédient de secours 3',
    strIngredient4: 'Ingrédient de secours 4',
    strIngredient5: 'Ingrédient de secours 5',
    strIngredient6: 'Ingrédient de secours 6',
    strIngredient7: 'Ingrédient de secours 7',
    strIngredient8: 'Ingrédient de secours 8',
    strIngredient9: 'Ingrédient de secours 9',
    strIngredient10: 'Ingrédient de secours 10',
    strIngredient11: 'Ingrédient de secours 11',
    strIngredient12: 'Ingrédient de secours 12',
    strIngredient13: 'Ingrédient de secours 13',
    strIngredient14: 'Ingrédient de secours 14',
    strIngredient15: 'Ingrédient de secours 15',
    strIngredient16: 'Ingrédient de secours 16',
    strIngredient17: 'Ingrédient de secours 17',
    strIngredient18: 'Ingrédient de secours 18',
    strIngredient19: 'Ingrédient de secours 19',
    strIngredient20: 'Ingrédient de secours 20',
    strMeasure1: 'Mesure de secours 1',
    strMeasure2: 'Mesure de secours 2',
    strMeasure3: 'Mesure de secours 3',
    strMeasure4: 'Mesure de secours 4',
    strMeasure5: 'Mesure de secours 5',
    strMeasure6: 'Mesure de secours 6',
    strMeasure7: 'Mesure de secours 7',
    strMeasure8: 'Mesure de secours 8',
    strMeasure9: 'Mesure de secours 9',
    strMeasure10: 'Mesure de secours 10',
    strMeasure11: 'Mesure de secours 11',
    strMeasure12: 'Mesure de secours 12',
    strMeasure13: 'Mesure de secours 13',
    strMeasure14: 'Mesure de secours 14',
    strMeasure15: 'Mesure de secours 15',
    strMeasure16: 'Mesure de secours 16',
    strMeasure17: 'Mesure de secours 17',
    strMeasure18: 'Mesure de secours 18',
    strMeasure19: 'Mesure de secours 19',
    strMeasure20: 'Mesure de secours 20',
  };
};

/**
 * Télécharge et sauvegarde l'image d'une recette localement
 * @param {string} imageUrl - L'URL de l'image à télécharger
 * @param {string} recipeName - Le nom de la recette pour générer un nom de fichier
 * @returns {Promise<string>} - Le chemin local de l'image sauvegardée
 */
const downloadAndSaveRecipeImage = async (imageUrl, recipeName) => {
  try {
    // Vérifier que l'URL est valide
    if (!imageUrl || !imageUrl.startsWith('http')) {
      throw new Error('URL d\'image invalide');
    }
    
    // Créer un nom de fichier sécurisé basé sur le nom de la recette
    const safeFileName = recipeName
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    // Déterminer l'extension du fichier à partir de l'URL
    const fileExtMatch = imageUrl.match(/\.(jpe?g|png|gif|webp)($|\?)/i);
    const fileExt = fileExtMatch ? fileExtMatch[1].toLowerCase() : 'jpg';
    
    // Chemin du dossier public pour les images
    const publicDir = 'public/images/recipes';
    const timestamp = Date.now();
    const filename = `${safeFileName}-${timestamp}.${fileExt}`;
    const filePath = `${publicDir}/${filename}`;
    
    // Vérifier si le dossier existe, sinon le créer
    const fs = require('fs');
    const path = require('path');
    const fullDirPath = path.resolve(process.cwd(), publicDir);
    
    if (!fs.existsSync(fullDirPath)) {
      fs.mkdirSync(fullDirPath, { recursive: true });
    }
    
    // Télécharger l'image
    const fullFilePath = path.resolve(process.cwd(), filePath);
    const https = require('https');
    const http = require('http');
    
    await new Promise((resolve, reject) => {
      const file = fs.createWriteStream(fullFilePath);
      const protocol = imageUrl.startsWith('https') ? https : http;
      
      protocol.get(imageUrl, (response) => {
        if (response.statusCode !== 200) {
          fs.unlink(fullFilePath, () => {});
          reject(new Error(`Erreur HTTP ${response.statusCode}`));
          return;
        }
        
        response.pipe(file);
        
        file.on('finish', () => {
          file.close();
          resolve();
        });
      }).on('error', (err) => {
        fs.unlink(fullFilePath, () => {});
        reject(err);
      });
    });
    
    // Retourner le chemin relatif pour l'accès via le navigateur
    return `/images/recipes/${filename}`;
  } catch (error) {
    console.error('Erreur lors du téléchargement de l\'image:', error);
    return null;
  }
};

/**
 * Convertit une recette de TheMealDB au format de notre application
 * @param {Object} mealDbRecipe - Recette de TheMealDB
 * @returns {Object} - Recette au format de notre application
 */
export const convertToTartinaFormat = (mealDbRecipe) => {
  // Extraire les ingrédients et leurs mesures
  const ingredients = [];
  for (let i = 1; i <= 20; i++) {
    const ingredient = mealDbRecipe[`strIngredient${i}`];
    const measure = mealDbRecipe[`strMeasure${i}`];
    
    if (ingredient && ingredient.trim()) {
      ingredients.push({
        name: ingredient,
        quantity: measure?.trim() || '',
        unit: '',
        cost: Math.floor(Math.random() * 500) + 100 // Coût estimé aléatoire entre 100 et 600
      });
    }
  }
  
  // Calculer le coût total estimé
  const totalCost = ingredients.reduce((sum, ing) => sum + (ing.cost || 0), 0);
  
  // Convertir les instructions en étapes
  const steps = mealDbRecipe.strInstructions
    .split('.')
    .filter(step => step.trim().length > 0)
    .map(step => step.trim() + '.');
  
  return {
    name: mealDbRecipe.strMeal,
    description: `Délicieuse recette de ${mealDbRecipe.strMeal} à base de chocolat, adaptée pour utiliser la pâte à tartiner Tartina.`,
    category: mealDbRecipe.strCategory?.toLowerCase() || 'dessert',
    event: 'weekend',
    prep_time: Math.floor(Math.random() * 30) + 10, // Temps de préparation aléatoire entre 10 et 40 minutes
    estimated_cost: totalCost,
    rating: (Math.random() * 2 + 3).toFixed(1), // Note aléatoire entre 3 et 5
    image_url: mealDbRecipe.strMealThumb,
    ingredients,
    steps,
    tips: [
      "Remplacez le chocolat par une quantité équivalente de Tartina pour un goût encore plus gourmand.",
      "Servez avec une boule de glace à la vanille pour encore plus de gourmandise."
    ],
    tartina_quantity: "4 cuillères"
  };
};

/**
 * Importe une recette externe dans notre base de données
 * @param {Object} recipe - Recette au format de notre application
 * @returns {Promise<Object>} - La recette importée
 */
export const importRecipeToDatabase = async (recipe) => {
  try {
    // Vérifier si l'image est une URL externe, si oui la télécharger
    if (recipe.image_url && recipe.image_url.startsWith('http')) {
      console.log(`Téléchargement de l'image pour ${recipe.name}...`);
      
      try {
        // Télécharger l'image
        const localImagePath = await downloadAndSaveRecipeImage(recipe.image_url, recipe.name);
        if (localImagePath) {
          recipe.image_url = localImagePath;
          console.log(`Image téléchargée avec succès: ${localImagePath}`);
        }
      } catch (imageError) {
        console.error(`Erreur lors du téléchargement de l'image pour ${recipe.name}:`, imageError);
        // Continuer l'importation même si l'image n'a pas pu être téléchargée
      }
    }
    
    const created = await recipeService.addRecipe(recipe);
    return created || null;
  } catch (error) {
    console.error('Erreur lors de l\'importation de la recette:', error);
    return null;
  }
};

/**
 * Importe plusieurs recettes à base de chocolat depuis TheMealDB
 * @param {number} limit - Nombre maximal de recettes à importer
 * @returns {Promise<Array>} - Les recettes importées
 */
export const importChocolateRecipes = async (limit = 5) => {
  try {
    // Essayer plusieurs termes de recherche liés au chocolat
    const searchTerms = ['chocolate', 'cocoa', 'brownie', 'cake', 'dessert'];
    let recipes = [];
    
    // Essayer chaque terme jusqu'à trouver des recettes
    for (const term of searchTerms) {
      console.log(`Tentative de recherche avec le terme: ${term}`);
      const results = await fetchRecipesByIngredient(term);
      if (results.length > 0) {
        recipes = results;
        console.log(`${results.length} recettes trouvées avec le terme: ${term}`);
        break;
      }
    }
    
    // Si aucune recette n'est trouvée, utiliser les recettes de secours
    if (recipes.length === 0) {
      console.warn('Aucune recette trouvée via l\'API, utilisation des recettes de secours');
      recipes = getFallbackRecipes();
    }
    
    const importedRecipes = [];
    
    // Limiter le nombre de recettes à importer
    const recipesToImport = recipes.slice(0, limit);
    
    for (const recipe of recipesToImport) {
      // Récupérer les détails de chaque recette
      const details = await fetchRecipeDetails(recipe.idMeal || recipe.id);
      if (details) {
        // Convertir au format de notre application
        const tartinaRecipe = convertToTartinaFormat(details);
        
        // Importer dans la base de données
        const imported = await importRecipeToDatabase(tartinaRecipe);
        if (imported) {
          importedRecipes.push(imported);
        }
      }
    }
    
    return importedRecipes;
  } catch (error) {
    console.error('Erreur lors de l\'importation des recettes:', error);
    return [];
  }
};

/**
 * Fournit des recettes de secours lorsque l'API externe ne répond pas
 * @returns {Array} - Liste de recettes de secours
 */
const getFallbackRecipes = () => [
  {
    id: 'fallback1',
    idMeal: 'fallback1',
    strMeal: 'Fondant au Chocolat Tartina',
    strMealThumb: 'https://images.unsplash.com/photo-1624353365286-3f8d62daad51?q=80&w=1000&auto=format&fit=crop'
  },
  {
    id: 'fallback2',
    idMeal: 'fallback2',
    strMeal: 'Cookies Moelleux au Chocolat',
    strMealThumb: 'https://images.unsplash.com/photo-1499636136210-6f4ee915583e?q=80&w=1000&auto=format&fit=crop'
  },
  {
    id: 'fallback3',
    idMeal: 'fallback3',
    strMeal: 'Brownie Gourmand',
    strMealThumb: 'https://images.unsplash.com/photo-1564355808539-22fda35bed7e?q=80&w=1000&auto=format&fit=crop'
  },
  {
    id: 'fallback4',
    idMeal: 'fallback4',
    strMeal: 'Gâteau Moelleux Tartina',
    strMealThumb: 'https://images.unsplash.com/photo-1605291566281-babc3dbe1d29?q=80&w=1000&auto=format&fit=crop'
  },
  {
    id: 'fallback5',
    idMeal: 'fallback5',
    strMeal: 'Muffins à la Tartina',
    strMealThumb: 'https://images.unsplash.com/photo-1576618148400-f03562e64548?q=80&w=1000&auto=format&fit=crop'
  },
  {
    id: 'fallback6',
    idMeal: 'fallback6',
    strMeal: 'Crêpes à la Tartina',
    strMealThumb: 'https://images.unsplash.com/photo-1519676867240-f03562e64548?q=80&w=1000&auto=format&fit=crop'
  }
];

// Exporter un objet nommé plutôt qu'un objet littéral direct
const externalRecipeService = {
  fetchRecipesByIngredient,
  fetchRecipeDetails,
  convertToTartinaFormat,
  importRecipeToDatabase,
  importChocolateRecipes,
  getFallbackRecipes,
  downloadAndSaveRecipeImage
};

export default externalRecipeService;
